import { useEffect, useState, useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Navigate } from 'react-router-dom';
import { PACKS_COLLECTION_LIVE } from '@/config/features';
import { Loader2 } from 'lucide-react';
import { PackOpeningScene } from '@/components/pack-opening/PackOpeningScene';
import type { RevealProof } from '@/components/pack-opening/ProvablyFairChip';
import { API_BASE } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useToast } from '@/stores/ui.store';

type Rarity = 'PROSPECT' | 'ROOKIE' | 'VETERAN' | 'ALL_STAR' | 'GOAT';

interface Card {
  playerId: string;
  playerName: string;
  team: string;
  position: string;
  rarity: Rarity;
  imageUrl: string | null;
}

export default function PackOpenPage() {
  // Not live yet — see PACKS_COLLECTION_LIVE. <Navigate> keeps the memory
  // router in charge (redirect() from the shim unloads the SPA).
  // PACKS_COLLECTION_LIVE is a build-time constant, so the early return
  // never varies between renders (hooks order is safe).
  if (!PACKS_COLLECTION_LIVE) return <Navigate to="/" replace />;
  const params = useParams();
  const router = useRouter();
  const { isAuthenticated, accessToken } = useAuthStore();
  const { error: showError } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [cards, setCards] = useState<Card[] | null>(null);
  const [packName, setPackName] = useState('');
  // NFL reveals carry a fairness proof; the pack sport drives the art treatment.
  const [proof, setProof] = useState<RevealProof | null>(null);
  const [sport, setSport] = useState('NBA');
  const [error, setError] = useState<string | null>(null);

  const hasAttempted = useRef(false);

  const packPurchaseId = params.id as string;

  useEffect(() => {
    // Prevent multiple attempts
    if (hasAttempted.current) return;

    if (!isAuthenticated || !accessToken) {
      router.push('/login');
      return;
    }

    hasAttempted.current = true;

    const revealPack = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/packs/${packPurchaseId}/reveal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const data = await res.json();

        if (!res.ok) {
          setError(data.error || 'Failed to open pack');
          return;
        }

        setCards(data.cards);
        setPackName(data.packName || 'Card Pack');
        // NFL reveals return the fairness proof; its presence also selects the
        // NFL art treatment (line-art, class chips, ruby seal).
        if (data.proof) {
          setProof(data.proof as RevealProof);
          setSport('NFL');
        }
      } catch (err) {
        setError('Something went wrong');
      } finally {
        setIsLoading(false);
      }
    };

    revealPack();
  }, [packPurchaseId, isAuthenticated, accessToken, router]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">Preparing your pack...</p>
        </div>
      </div>
    );
  }

  if (error || !cards) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Pack not found'}</p>
          <button
            onClick={() => router.push('/collection?tab=packs')}
            className="text-amber-500 hover:text-amber-400"
          >
            Back to Collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <PackOpeningScene
      packId={packPurchaseId}
      packName={packName}
      cards={cards}
      sport={sport}
      proof={proof}
      onComplete={() => router.push('/collection')}
    />
  );
}
